import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {cancelOfflineTaskAsyncAction} from '../../../../srcCommon/tasks/taskActions';
import {isServerMessage} from '../../../helpers/models/messageHelpers';
import {ApiConversationDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {MessageModel, MessageStatusesEnum, MessageVisualStatusesEnum} from '../../../models/api/messageModel';
import {postApiAsync} from '../../../services/http/http';
import {Dispatch, StateGetter} from '../../appStore';
import {ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {popoutComposerAction} from '../../panels/composer/composerPanelActions';
import {updateDataAction} from '../dataGroupsActions';
import {getMessages} from '../dataSelectors';
import {DataState} from '../dataState';

/*
 * Action creator.
 */

export function cancelMessageAsyncAction(messageUid: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    await dispatch(
      optimisticUpdateAsyncAction(
        createObjectPropertyUpdater<DataState, 'messages'>(
          'messages',
          createResourceMapValueUpdater(messageUid, (message) => {
            if (
              message.visualStatus !== MessageVisualStatusesEnum.DRAFT ||
              (message.status !== MessageStatusesEnum.DRAFT_SENDING &&
                message.status !== MessageStatusesEnum.DRAFT_PENDING &&
                message.status !== MessageStatusesEnum.DRAFT_LATER)
            ) {
              return message;
            }

            return {
              ...message,
              status: MessageStatusesEnum.DRAFT_COMPOSE,
            };
          }),
        ),
        async () => {
          const message = findMessage(getState, messageUid);
          const [dataGroup] = await Promise.all([
            maybeCancelMessageAsync(message),
            tryMaybeCancelOfflineTaskAsync(dispatch, message),
          ]);

          return updateDataAction(dataGroup);
        },
      ),
    );

    // If everything went right and it's not a reply, open a popout composer.
    const message = findMessage(getState, messageUid);
    if (message && message.status === MessageStatusesEnum.DRAFT_COMPOSE && !message.isReply) {
      dispatch(popoutComposerAction(message.uid));
    }
  };
}

/*
 * Helpers.
 */

function findMessage(getState: StateGetter, messageUid: string): MessageModel | undefined {
  const state = getState();
  const messages = getMessages(state);
  return messages[messageUid];
}

async function maybeCancelMessageAsync(message: MessageModel | undefined): Promise<DataGroupModel> {
  if (!message || !isServerMessage(message)) {
    return {};
  }

  const url = `${message.url}/cancel?new=front`;
  return postApiAsync(ApiConversationDataGroup, url, {});
}

async function tryMaybeCancelOfflineTaskAsync(
  dispatch: Dispatch,
  message: MessageModel | undefined,
): Promise<void> {
  const taskId =
    message && message.visualStatus === MessageVisualStatusesEnum.DRAFT
      ? message.sendOfflineTaskId
      : undefined;

  if (!taskId) {
    return;
  }

  try {
    await dispatch(cancelOfflineTaskAsyncAction(taskId));
  } catch (error) {
    reportToBugsnag(error);
  }
}
